<template>
  <div class="page-container">
    <!-- 顶部标题 + 搜索 -->
    <div class="header-bar">
      <h1 class="title">🌿 植物管理中心</h1>
      <el-input
          v-model="searchQuery"
          placeholder="搜索植物名称..."
          clearable
          prefix-icon="Search"
          class="search-input"
      />
    </div>

    <!-- 操作区 -->
    <el-card class="action-card">
      <div class="button-group">
        <!-- 生成分组按钮 -->
        <el-button type="primary" @click="generateGroup" :loading="loading">
          {{ loading ? '生成中...' : '生成分组' }}
        </el-button>

        <!-- 添加植物按钮 -->
        <el-button type="success" @click="goToAddPlant">
          ➕ 添加新的植物
        </el-button>
      </div>
      <p class="tip-text">
        点击“生成分组”自动调用后端接口；点击“添加植物”进入新增页面。
      </p>

      <!-- 生成结果显示区 -->
      <div v-if="groupStatus.message" class="group-status" :class="groupStatus.type">
        {{ groupStatus.message }}
      </div>
    </el-card>

    <!-- 植物展示区 -->
    <h2 class="sub-title">已添加的植物</h2>

    <div class="plant-grid">
      <el-card
          v-for="plant in filteredPlants"
          :key="plant.id"
          class="plant-card"
          shadow="hover"
      >
        <img :src="plant.image" alt="plant" class="plant-image" />
        <div class="plant-info">
          <h3>{{ plant.name }}</h3>
          <div class="action-buttons">
            <el-button size="small" type="primary" @click="goToEditPage(plant.id)">编辑</el-button>
            <el-button size="small" type="danger" @click="deletePlant(plant.id)">删除</el-button>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import api from '@/services/api';

const router = useRouter()
const goToEditPage = (id) => {
  router.push(`/plants/update/${id}`)
}

// 状态
const loading = ref(false)
const plants = ref([])
const searchQuery = ref('')

// 编辑弹窗
const editDialogVisible = ref(false)
const editForm = ref({ id: null, name: '', image: '' })

// 分组结果状态
const groupStatus = ref({
  message: '',
  type: '' // 可选值: 'success', 'warning', 'error'
})

// 计算属性：根据搜索关键词过滤
const filteredPlants = computed(() => {
  if (!searchQuery.value) return plants.value
  return plants.value.filter(p =>
      p.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

// 🧩 跳转添加页面
const goToAddPlant = () => {
  router.push('/add_plant')
}

// 🧩 获取植物列表
const getPlants = async () => {
  try {
    const res = await api.get('/plants/list');
    plants.value = res.data.data
  } catch (err) {
    // 模拟数据
    plants.value = [
      { id: 1, name: '玫瑰-模拟数据', image: 'https://picsum.photos/300/200?random=1' },
      { id: 2, name: '仙人掌-模拟数据', image: 'https://picsum.photos/300/200?random=2' },
      { id: 3, name: '绿萝-模拟数据', image: 'https://picsum.photos/300/200?random=3' },
      { id: 4, name: '吊兰-模拟数据', image: 'https://picsum.photos/300/200?random=4' },
      { id: 5, name: '兰花-模拟数据', image: 'https://picsum.photos/300/200?random=5' },
      { id: 6, name: '竹子-模拟数据', image: 'https://picsum.photos/300/200?random=6' },
      { id: 7, name: '多肉-模拟数据', image: 'https://picsum.photos/300/200?random=7' },
      { id: 8, name: '薄荷-模拟数据', image: 'https://picsum.photos/300/200?random=8' },
    ]
  }
}

// 🧩 编辑植物
const openEditDialog = (plant) => {
  editForm.value = { ...plant }
  editDialogVisible.value = true
}
const saveEdit = async () => {
  try {
    await axios.put(`/plants/update/${editForm.value.id}`, editForm.value)
    const index = plants.value.findIndex(p => p.id === editForm.value.id)
    if (index !== -1) plants.value[index] = { ...editForm.value }
    editDialogVisible.value = false
    ElMessage.success('植物信息已更新')
  } catch (err) {
    console.error('保存失败', err)
    ElMessage.error('保存失败')
  }
}

// 🧩 删除植物
const deletePlant = async (id) => {
  try {
    await api.delete(`/plants/delete/${id}`)
    plants.value = plants.value.filter(p => p.id !== id)
    ElMessage.success('删除成功')
  } catch (err) {
    console.error('删除植物失败', err)
    ElMessage.error('删除失败')
  }
}

onMounted(getPlants)
</script>

<style scoped>
.page-container {
  padding: 20px 40px;
  width: 100%;
  box-sizing: border-box;
}
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.title {
  font-size: 28px;
  font-weight: 600;
  color: #2c3e50;
}
.search-input {
  width: 250px;
}
.action-card {
  margin-bottom: 30px;
}
.button-group {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 10px;
}
.tip-text {
  text-align: center;
  font-size: 14px;
  color: #666;
}
.group-status {
  margin-top: 15px;
  padding: 10px;
  border-radius: 6px;
  text-align: center;
  font-weight: 500;
}
.group-status.success {
  background-color: #ecfdf5;
  color: #047857;
}
.group-status.warning {
  background-color: #fffbeb;
  color: #92400e;
}
.group-status.error {
  background-color: #fef2f2;
  color: #991b1b;
}
.sub-title {
  margin: 20px 0 10px 10px;
  font-size: 20px;
  font-weight: 500;
  color: #333;
}
.plant-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  width: 100%;
}
.plant-card {
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s;
}
.plant-card:hover {
  transform: translateY(-4px);
}
.plant-image {
  width: 100%;
  height: 160px;
  object-fit: cover;
}
.plant-info {
  text-align: center;
  padding: 10px;
}
.action-buttons {
  margin-top: 8px;
  display: flex;
  justify-content: center;
  gap: 8px;
}
</style>
